import { Blac, Cubit } from "blac-next";
import { isHybridApp } from "src/lib/platform";
import { onUserSessionReset } from "src/lib/onUserSessionReset";
import MealRemindersCubit from "src/state/MealRemindersCubit/MealRemindersCubit";
import {
  type CalibrationEndChoice,
  DEFAULT_MEAL_REMINDER_SCHEDULE,
  type MealReminderSchedule,
  type MealReminderSlot,
  type MealReminderTime,
  scheduleTrackingData
} from "src/state/MealRemindersCubit/mealRemindersHelpers";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import { pushNotificationState, tracker } from "src/state/state";
import { TrackEvent } from "src/state/Track/TrackCubit";

export type MealReminderFlowView = "none" | "prompt" | "editor" | "downloadApp";
export type MealReminderEditorMode = "firstTime" | "edit";

export interface MealReminderFlowState {
  view: MealReminderFlowView;
  editorMode: MealReminderEditorMode;
  draft: MealReminderSchedule;
  pickingSlot?: MealReminderSlot;
  saving: boolean;
}

export const PROMPT_SEEN_STORAGE_KEY = "meal-reminders-prompt-seen";

const buildInitialState = (): MealReminderFlowState => ({
  view: "none",
  editorMode: "firstTime",
  draft: DEFAULT_MEAL_REMINDER_SCHEDULE,
  saving: false
});

/** The reminder prompt, editor and download sheet, shown over any app page. */
export default class MealReminderFlowCubit extends Cubit<MealReminderFlowState> {
  static keepAlive = true;

  constructor() {
    super(buildInitialState());

    onUserSessionReset(() => {
      this.emit(buildInitialState());
    });
  }

  private get reminders(): MealRemindersCubit {
    return Blac.getBloc(MealRemindersCubit);
  }

  get isWebOnly(): boolean {
    return !isHybridApp();
  }

  private readonly promptSeen = (): boolean =>
    StorageController.getItem(PROMPT_SEEN_STORAGE_KEY) === "true";

  /**
   * Shown once, on the first meal for today during calibration, since it promises
   * today's insights. A backdated meal doesn't count.
   */
  readonly promptAfterMealSave = async (): Promise<void> => {
    await this.reminders.load();
    const { hasSavedSchedule, phase, mealsToday } = this.reminders.state;
    if (
      hasSavedSchedule ||
      phase !== "calibration" ||
      mealsToday !== 1 ||
      this.promptSeen()
    ) {
      return;
    }
    StorageController.setItem(PROMPT_SEEN_STORAGE_KEY, "true");
    this.patch({ view: "prompt" });
    tracker.track(TrackEvent.MealReminderPromptDisplayed, {
      data: { webOnly: this.isWebOnly }
    });
  };

  readonly acceptPrompt = (): void => {
    if (this.isWebOnly) {
      this.openDownloadSheet();
      return;
    }
    this.openEditor();
  };

  readonly dismissPrompt = (): void => {
    this.patch({ view: "none" });
    tracker.track(TrackEvent.MealReminderPromptDismissed);
  };

  readonly openEditor = (): void => {
    const { schedule, hasSavedSchedule } = this.reminders.state;
    this.patch({
      view: "editor",
      editorMode: hasSavedSchedule ? "edit" : "firstTime",
      draft: schedule,
      pickingSlot: undefined
    });
    tracker.track(TrackEvent.MealReminderEditorOpened, {
      data: { firstTime: !hasSavedSchedule }
    });
  };

  /** Mobile web can't send pushes, so it points to the app instead of the editor. */
  readonly openReminders = (isDesktop: boolean): void => {
    if (this.isWebOnly && !isDesktop) {
      this.openDownloadSheet();
      return;
    }
    this.openEditor();
  };

  private readonly trackCalibrationEndChoice = (
    choice: CalibrationEndChoice
  ): void => {
    tracker.track(TrackEvent.MealReminderCalibrationEndChoice, {
      data: { choice }
    });
  };

  readonly keepRemindersOn = async (): Promise<void> => {
    this.trackCalibrationEndChoice("keepOn");
    await this.reminders.keepRemindersOn();
  };

  readonly adjustReminders = (): void => {
    this.trackCalibrationEndChoice("adjust");
    this.reminders.recordCalibrationEndChoice("adjust");
    this.openEditor();
  };

  /** Reminders were off at the end of calibration and the member opens the editor to set them. */
  readonly setRemindersAfterCalibration = (): void => {
    this.trackCalibrationEndChoice("setReminders");
    this.reminders.recordCalibrationEndChoice("setReminders");
    this.openReminders(false);
  };

  readonly openDownloadSheet = (): void => {
    this.patch({ view: "downloadApp" });
    tracker.track(TrackEvent.MealReminderDownloadSheetOpened);
  };

  readonly close = (): void => {
    this.patch({ view: "none", pickingSlot: undefined });
  };

  readonly updateDraftSlot = (
    slot: MealReminderSlot,
    change: Partial<MealReminderTime>
  ): void => {
    const { draft } = this.state;
    this.patch({ draft: { ...draft, [slot]: { ...draft[slot], ...change } } });
  };

  readonly openTimePicker = (slot: MealReminderSlot): void => {
    this.patch({ pickingSlot: slot });
  };

  readonly closeTimePicker = (): void => {
    this.patch({ pickingSlot: undefined });
  };

  /** Settings edits save straight away instead of going through the draft. */
  readonly updateSavedSlot = async (
    slot: MealReminderSlot,
    change: Partial<MealReminderTime>
  ): Promise<void> => {
    const { schedule } = this.reminders.state;
    const saved = await this.reminders.saveSchedule({
      ...schedule,
      [slot]: { ...schedule[slot], ...change }
    });
    if (saved && change.enabled && !this.isWebOnly) {
      await pushNotificationState.requestPermissions();
    }
  };

  readonly debugShowPrompt = (): void => {
    StorageController.removeItem(PROMPT_SEEN_STORAGE_KEY);
    this.patch({ view: "prompt" });
  };

  readonly confirmEditor = async (): Promise<void> => {
    if (this.state.saving) {
      return;
    }
    this.patch({ saving: true });
    try {
      const saved = await this.reminders.saveSchedule(this.state.draft);
      if (!saved) {
        return;
      }
      tracker.track(TrackEvent.MealReminderScheduleSaved, {
        data: scheduleTrackingData(this.state.draft)
      });
      this.patch({ view: "none", pickingSlot: undefined });
      if (!this.isWebOnly) {
        await pushNotificationState.requestPermissions();
      }
    } finally {
      this.patch({ saving: false });
    }
  };
}
